import React from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';

import FunctionsIcon from '@mui/icons-material/Functions';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  Stack,
  Dialog,
  Button,
  Divider,
  TextField,
  Typography,
  DialogContent,
  DialogActions,
} from '@mui/material';

export function CriteriaSummary({ criteriaList }) {
  // Use the real criteriaList prop, not demo data
  const list = criteriaList || [];
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Criteria Summary
      </Typography>
      <Stack
        spacing={1.5}
        sx={{
          maxHeight: 200,
          overflowY: 'auto',
        }}
        className="my-custom-scrollbar"
      >
        {list && list.length > 0 ? (
          list.map((criteria, idx) => (
            <Stack
              key={criteria._id || idx}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              sx={{ p: 1, border: '1px solid #eee', borderRadius: 1 }}
              flexWrap="wrap"
            >
              <Typography variant="body2" color="primary" sx={{ minWidth: 20 }}>
                {idx === 0 ? 'IF' : 'AND'}
              </Typography>
              {/* Show all conditions for this criteria */}
              {criteria.conditions &&
                criteria.conditions.map((cond, cidx) => (
                  <React.Fragment key={cidx}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#fff',
                        border: '1px solid rgba(212, 235, 255, 0.83)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        minWidth: 90,
                      }}
                    >
                      <FunctionsIcon
                        fontSize="small"
                        color="primary"
                        sx={{ mr: 0.5, fontSize: 14 }}
                      />
                      <Typography variant="caption" fontWeight={600}>
                        {cond.metric}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.2,
                        borderRadius: 1,
                        bgcolor: '#f4f6f8',
                        border: '1px solid #f4f6f8',
                        minWidth: 60,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="caption" fontWeight={500}>
                        {cond.operator}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: '#FEE8D6',
                        border: '1px solid #FEE8D6',
                        minWidth: 60,
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        {cond.value}
                      </Typography>
                    </Box>
                  </React.Fragment>
                ))}
              <Typography
                variant="caption"
                color="secondary"
                sx={{ minWidth: 40, fontWeight: 'bold' }}
              >
                THEN
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#e8f5e9',
                  border: '1px solid #e8f5e9',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  minWidth: 80,
                }}
              >
                <ArrowForwardIcon fontSize="small" color="success" sx={{ mr: 0.5, fontSize: 14 }} />
                <Typography variant="caption" fontWeight={600}>
                  {criteria.action?.actionType || '--'}
                  {criteria.action?.value !== null && criteria.action?.value !== undefined
                    ? ` (${criteria.action.value})`
                    : ''}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1} ml={2}>
                <AccessTimeIcon fontSize="small" sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                <Typography variant="caption" color="text.secondary">
                  {criteria.lookbackPeriod?.title || ''}
                </Typography>
                <RemoveCircleOutlineIcon
                  fontSize="small"
                  sx={{ fontSize: 14, verticalAlign: 'middle', ml: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {criteria.exclude || 'None'}
                </Typography>
              </Stack>
            </Stack>
          ))
        ) : (
          <Typography variant="body2">No criteria defined.</Typography>
        )}
      </Stack>
    </Box>
  );
}

CriteriaSummary.propTypes = {
  criteriaList: PropTypes.array,
};

export default function SaveRuleTemplateModal({
  open,
  onClose,
  onSubmit,
  criteriaList,
  initialTitle,
  initialDescription,
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: initialTitle || '',
      description: initialDescription || '',
    },
  });

  const handleSave = (data) => {
    onSubmit({ ...data, criteria: criteriaList });
    onClose();
  };

  console.log(criteriaList);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleSave)}>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="subtitle2">Save Rule Template</Typography>
          <Stack spacing={2} mt={2}>
            <TextField
              label="Title"
              {...register('title', { required: true })}
              error={!!errors.title}
              helperText={errors.title && 'Title is required'}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              {...register('description')}
              fullWidth
              size="small"
              multiline
              rows={4}
            />
            <Divider sx={{ my: 1 }} />
            <CriteriaSummary criteriaList={criteriaList} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="success">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

SaveRuleTemplateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  criteriaList: PropTypes.array,
  initialTitle: PropTypes.string,
  initialDescription: PropTypes.string,
};
